package gr.tech.site.pages.states

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.components.states.UtilityCardData
import gr.tech.site.components.states.UtilityPartnerGrid
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.states.OregonHeroTextStyle
import gr.tech.site.styles.states.StateStatIconStyle
import gr.tech.site.styles.states.StateStatLabelStyle
import gr.tech.site.styles.states.StateStatNumberStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initOregonPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Oregon - LED Grow Light & HVAC Energy Rebates for Growers",
        description = "Energy Trust of Oregon administers energy rebates on LED grow lights and HVAC for commercial horticulture growers. Green Rebates captures every incentive.",
        canonicalUrl = "https://greenrebates.com/states/oregon",
        ogImage = "/images/oregon1.jpg"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun OregonPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        )
        {
            Img(
                src = ImageRepo.Oregon1.src,
                alt = ImageRepo.Oregon1.alt,
                attrs = Modifier.fillMaxSize().toAttrs {
                    attr("fetchpriority", "high")
                    style { objectFit(ObjectFit.Cover) }
                }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Black)
                    .opacity(30.percent)
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .then(OregonHeroTextStyle.toModifier())
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) { Text("Oregon Horticulture Energy Rebates") }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .then(OregonHeroTextStyle.toModifier())
                        .classNames("mb-3").toAttrs()
                ) { Text("Oregon Growers: Unlock LED Horticulture & HVAC Energy Rebates") }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .then(OregonHeroTextStyle.toModifier())
                        .classNames("mb-4", "fw-medium", "text-white").toAttrs()
                ) {
                    Text("Oregon has one of the most established cannabis and commercial horticulture industries in the country. Green Rebates works directly with Energy Trust of Oregon to help cultivators — from Willamette Valley greenhouse operators to Southern Oregon cannabis facilities — maximize LED horticulture rebates, HVAC incentives, and dehumidification funding.")
                }

                Link(
                    path = "/contact-us?state=Oregon",
                    text = "Free Rebates Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free Oregon grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // TYPES OF REBATES AVAILABLE — immediately after hero, no preamble
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) { Text("Oregon Grower Rebates & Incentives") }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Oregon's temperate, high-humidity climate creates real energy demands for grow facilities — especially for dehumidification and climate control. Energy Trust of Oregon's commercial energy efficiency programs are designed to offset those costs.")
                }
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4")
                        .toAttrs()
                ) {
                    listOf(
                        "LED Grow Light Rebates" to
                            "Oregon cannabis cultivators and greenhouse operators switching from HPS or HID to DLC-qualified LED horticulture lighting can recover a significant portion of retrofit costs through Energy Trust of Oregon. Oregon's mature grow sector means DLC qualification is well-established here.",
                        "Commercial HVAC Incentives" to
                            "Oregon's wet climate — especially west of the Cascades — makes precision humidity and temperature management non-negotiable. Energy Trust of Oregon HVAC rebates help licensed cultivators across the state offset equipment and installation costs.",
                        "Dehumidification Rebates" to
                            "Western Oregon's persistently high ambient humidity makes dehumidification a major energy draw in any grow facility. Energy Trust of Oregon dehumidification incentives help Willamette Valley and coastal operators cut operating costs and get paid for the upgrade.",
                        "Custom Grow & Greenhouse Programs" to
                            "Energy Trust of Oregon offers custom incentive programs for large cannabis operations, commercial nurseries, and greenhouse facilities that exceed standard prescriptive rebate thresholds — a common fit for Oregon's larger horticultural operations."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHY NOW — urgency callout
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-7")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) { Text("Oregon Growers Can't Afford to Wait") }
                P(
                    attrs = LandingDarkBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto").toAttrs()
                ) {
                    Text("Oregon's cannabis and horticultural industry has been drawing from Energy Trust of Oregon's rebate programs for years — which means experienced operators know to move before funding closes. Every grow season you delay an LED retrofit or HVAC upgrade is a rebate cycle permanently missed. Green Rebates gets your pre-approval in before the queue fills.")
                }
                Link(
                    path = "/contact-us?state=Oregon",
                    text = "Free Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaOutlineStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Check your Oregon grow light and HVAC rebates now") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // WHO QUALIFIES — split image + checklist
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {
                    // Image on right for Oregon (flipped from Washington)
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-2", "order-2").toAttrs()) {
                        Img(
                            src = ImageRepo.LEDGrowLights.src,
                            alt = ImageRepo.LEDGrowLights.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "order-lg-1", "order-1", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Who Qualifies for Oregon Grow Rebates?")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Oregon has a large and mature cannabis industry, with growers of all sizes operating across the state. If your facility is served by an Energy Trust of Oregon participating utility, Green Rebates will find every available rebate, incentive, and energy savings program for your operation.")
                            }
                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "OLCC-licensed cannabis and marijuana cultivators in Energy Trust of Oregon's service territory",
                                    "Indoor, mixed-light, and greenhouse grow operations across Oregon",
                                    "Commercial nurseries and horticultural facilities upgrading to LED fixtures",
                                    "Operations adding dehumidification to manage western Oregon's wet climate"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }
                            Link(
                                path = "/contact-us?state=Oregon",
                                text = "Find Out If Your Facility Qualifies \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out if your Oregon grow facility qualifies for rebates") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }
                }
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) { Text("How to Claim Horticulture Rebates in Oregon") }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) { Text("Three steps to unlock LED grow light rebates, HVAC incentives, and dehumidification funding from Energy Trust of Oregon's Oregon programs.") }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        Triple("1", "We Audit Your Facility for Every Available Rebate",
                            "LED lighting, HVAC, dehumidification — we review your facility setup against every available Energy Trust of Oregon program before you purchase a single fixture."),
                        Triple("2", "We Submit and Manage Every Application",
                            "Oregon utility rebate programs require precise pre-approval documentation. We handle submissions, follow-ups, and Energy Trust of Oregon communication from start to finish — nothing falls through."),
                        Triple("3", "Your Rebate Check Arrives",
                            "Once Energy Trust of Oregon approves your project, your check arrives. No utility phone tag, no missed deadlines — just recovered capital on upgrades you were already planning.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Link(
                        path = "/contact-us?state=Oregon",
                        text = "Contact Us Now \u2192",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaBlackOutlineStyle.toModifier().classNames("mx-auto")
                            .role("button")
                            .attrsModifier { attr("aria-label", "Contact Us") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) { Text("Why Oregon Growers Choose Green Rebates") }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        "Energy Trust of Oregon Program Expertise" to
                            "We know every LED horticulture, HVAC, and dehumidification incentive available through Energy Trust of Oregon — including custom pathways most growers don't know to ask about.",
                        "Built for Oregon's Grow Industry" to
                            "Oregon's cannabis and horticultural sector is mature and competitive. We serve cultivators at every scale — from boutique indoor operations to large commercial nurseries and OLCC-licensed greenhouse facilities.",
                        "DLC Product Guidance Before You Buy" to
                            "We identify DLC-qualified fixtures and high-efficiency HVAC equipment before you spend a dollar — ensuring full Energy Trust of Oregon rebate eligibility and maximum recovery on your Oregon project.",
                        "Oregon Incentive Stacking" to
                            "Energy Trust of Oregon rebates are the foundation, not the ceiling. We identify every stackable state, federal, and utility incentive available to your Oregon operation so nothing gets left on the table.",
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }

                P(attrs = LandingMutedWhiteBodyStyle.toModifier().classNames("text-center").toAttrs()) {
                    Text("Oregon growers deserve a partner who knows the state's industry — and Energy Trust of Oregon's programs — as well as they do. That's us.")
                }
            }
        }

        // TRUSTED UTILITIES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "pt-5", "pb-3")
                .id("utilities")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "pb-1").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) { Text("Oregon Utility Rebate Programs") }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Green Rebates works directly with Energy Trust of Oregon to secure LED grow light rebates, commercial HVAC incentives, and dehumidification funding for Oregon's cannabis cultivators, greenhouses, and commercial nurseries. " +
                            "Energy Trust of Oregon serves customers statewide — we know the programs and we know the operators.")
                }

                UtilityPartnerGrid(listOf(
                    UtilityCardData(utilityName = "Energy Trust of Oregon", state = "Oregon", utilityParam = "energy-trust-oregon")
                ))
            }
        }

        // STATS + TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            H2(
                attrs = LandingSectionHeadingStyle.toModifier()
                    .classNames("mb-2").toAttrs()
            ) {
                Text("See How We Have Impacted ")
                SpanText(
                    text = "Oregon",
                    modifier = Modifier.classNames("fw-italic").color(Theme.MediumGreen.rgb)
                )
            }
            P(
                attrs = LandingSectionSubtitleStyle.toModifier()
                    .classNames("mb-4").toAttrs()
            ) {
                Text("From the Willamette Valley to the high desert, Oregon growers are cashing in on energy rebates.")
            }

            Div(attrs = Modifier.classNames("container", "mb-5").toAttrs()) {
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "justify-content-center")
                        .toAttrs()
                ) {
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaDollarSign(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("$55M+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Total Rebates Secured") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaClipboardList(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("400+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Projects Completed") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaBolt(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("420M") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("kWh Saved") }
                        }
                    }
                }
            }

            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates recovered over \$60,000 for our Oregon cannabis cultivation facility on LED grow light and HVAC upgrades through Energy Trust of Oregon. They handled every application — we just showed up to collect the check." to
                            "Licensed Cannabis Cultivator (Oregon)",
                        "I didn't think utility rebates could cover this much of our LED lighting costs. Green Rebates managed every application for our Oregon marijuana grow from start to finish. I'd recommend them to any cannabis operator in the state." to
                            "Marijuana Grower (Oregon)"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1").toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("- $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        // FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) { Text("FAQs About Energy Rebates in Oregon") }

                FaqAccordion(
                    accordionId = "oregon-faq",
                    items = listOf(
                        FaqItem(
                            question = "Which Oregon grow operations qualify for Energy Trust of Oregon energy rebates?",
                            answer = "OLCC-licensed cannabis and marijuana cultivators, greenhouse operators, indoor horticultural facilities, and commercial nurseries in Energy Trust of Oregon's Oregon service territory may qualify. If your operation has meaningful LED lighting or HVAC loads, there's very likely a rebate available."
                        ),
                        FaqItem(
                            question = "How much does Energy Trust of Oregon pay back on LED grow light upgrades?",
                            answer = "Rebate amounts are calculated based on the number of fixtures replaced and the wattage reduction achieved. DLC-qualified LED grow lights typically generate strong per-fixture rebates. Green Rebates reviews your planned upgrade and gives you a firm estimate before you commit to any equipment purchase."
                        ),
                        FaqItem(
                            question = "Do Oregon cannabis and marijuana facilities qualify for Energy Trust of Oregon rebates?",
                            answer = "Yes. Oregon legalized recreational cannabis in 2015 and OLCC-licensed cultivators qualify for Energy Trust of Oregon's commercial rebate programs like any other horticulture operation. LED grow lights, HVAC systems, dehumidification upgrades, and mixed-light greenhouse conversions are all eligible."
                        ),
                        FaqItem(
                            question = "Does Oregon require pre-approval before purchasing LED grow lights?",
                            answer = "Yes. Energy Trust of Oregon requires pre-approval before equipment purchase on most rebate programs. Buying equipment first disqualifies the project entirely. Green Rebates handles pre-approval submissions and timing so your Oregon facility never loses a rebate due to a missed step."
                        ),
                        FaqItem(
                            question = "What's the typical rebate for an Oregon grow facility?",
                            answer = "It varies significantly by operation size and scope. Oregon cannabis cultivators, greenhouse operators, and commercial nurseries have received anywhere from \$20,000 to over \$150,000 back on combined LED lighting and HVAC projects. Green Rebates gives you a realistic estimate based on your specific facility and Energy Trust of Oregon program eligibility."
                        )
                    )
                )
            }
        }

        RelatedSection(listOf(
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) { Text("Ready to Claim Your Oregon Grow Rebates?") }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto").toAttrs()
                ) {
                    Text("Oregon's LED horticulture rebates and HVAC incentives through Energy Trust of Oregon are some of the strongest in the region — but they don't roll over year to year. Green Rebates manages every pre-approval, application, and follow-up so your cultivation facility doesn't leave a dollar on the table.")
                }
                Link(
                    path = "/contact-us?state=Oregon",
                    text = "Get a Free Oregon Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free Oregon LED grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
